import duckify from '../../tools/duckify';
import { get as getSatellite } from '../../../common/satellites';

export const namespace = 'cassie';
export const store = 'mepbay';

export const Types = duckify(namespace, store, [
  'SET_SATELLITE',
  'SET_MISSION_FALLBACK',
  'SET_AOI',
  'SET_PERIOD',
  'SET_AVAILABLE_DATES',
  'LOAD_AVAILABLE_IMAGES',
  'ACQUIRE_IMAGE',
  'REQUEST_AOI',
  'LOAD_TEST_STATE',
  'LOAD_THUMBNAILS',
  'CANCEL_THUMBNAILS',
  'INSERT_METADATA',
]);

export const Actions = {
  /**
   * @function setSatellite
   * @description Builds the action selecting the satellite for this acquisition, also caching
   * its name/index to session storage.
   * @param {Number} satelliteIndex - Index of the satellite, per `common/satellites.js`.
   * **Called by:
   * @see {SatelliteChooser} - MEPBay module's satellite chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type SET_SATELLITE.
   */
  setSatellite: (satelliteIndex) => {
    window.sessionStorage.setItem('selectedSatellite', getSatellite(satelliteIndex).name);
    window.sessionStorage.setItem('selectedSatelliteIndex', satelliteIndex);
    return {
      type: Types.SET_SATELLITE,
      payload: {
        satellite: getSatellite(satelliteIndex),
        satelliteIndex,
      },
    };
  },

  /**
   * @function setTranningData
   * @description Caches the training data to session storage. Does not dispatch a Redux action
   * (no `type`/`payload` returned) and is not currently called via the MEPBay Actions object
   * anywhere in the codebase.
   * @param {Array} tranningData - Training data to cache.
   * @memberof module:Mepbay
   * @returns {void}
   */
  setTranningData: (tranningData) => {
    window.sessionStorage.setItem('tranningData', JSON.stringify(tranningData));
  },

  /**
   * @function setTranningDate
   * @description Caches the training date to session storage. Does not dispatch a Redux action
   * (no `type`/`payload` returned) and is not currently called via the MEPBay Actions object
   * anywhere in the codebase.
   * @param {*} tranningDate - Training date to cache.
   * @memberof module:Mepbay
   * @returns {void}
   */
  setTranningDate: (tranningDate) => {
    window.sessionStorage.setItem('tranningDate', JSON.stringify(tranningDate));
  },

  /**
   * @function setMissionFallback
   * @description Builds the action marking that the main mission cannot be used, so a secondary
   * collection should be used instead.
   * @param {Object} mission - Fallback mission configuration.
   * **Called by:
   * @see {handleLoadAvailableImages} - MEPBay module's saga.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type SET_MISSION_FALLBACK.
   */
  setMissionFallback: (mission) => ({
    type: Types.SET_MISSION_FALLBACK,
    payload: { mission },
  }),

  /**
   * @function setAOI
   * @description Builds the action defining the Area Of Interest to be used for this acquisition.
   * @param {*} overlay - The overlay from Google Maps.
   * @param {Array} coordinates - The coordinates of the area.
   * @param {*} geometry - The geometry of the AOI from Earth Engine.
   * **Called by:
   * @see {AOIChooser} - MEPBay module's AOI chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type SET_AOI.
   */
  setAOI: (overlay, coordinates, geometry) => ({
    type: Types.SET_AOI,
    payload: { overlay, coordinates, geometry },
  }),

  /**
   * @function loadAvailableImages
   * @description Builds the action that begins downloading the list of available images for the
   * currently selected satellite within the given period, storing it in the state afterwards.
   * @param {String} [startDate] restricts the search to images on/after this date; omit to search the mission's entire history
   * @param {String} [endDate] restricts the search to images on/before this date; omit to search up to today
   * **Called by:
   * @see {PeriodImageChooser} - MEPBay module's period/image chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type LOAD_AVAILABLE_IMAGES.
   */
  loadAvailableImages: (startDate, endDate) => ({
    type: Types.LOAD_AVAILABLE_IMAGES,
    payload: { startDate, endDate },
  }),

  /**
   * @function setAvailableDates
   * @description Builds the action setting the list of available dates (and their missions) for
   * the current satellite.
   * @param {Array<String>} dates - The list of dates, formatted as YYYY-mm-dd HH:mm:ss.
   * @param {Array} missions - Missions associated with the dates.
   * **Called by:
   * @see {PeriodImageChooser} - MEPBay module's period/image chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type SET_AVAILABLE_DATES.
   */
  setAvailableDates: (dates, missions) => ({
    type: Types.SET_AVAILABLE_DATES,
    payload: { dates, missions },
  }),

  /**
   * @function setPeriod
   * @description Builds the action defining the time period to be used in the image acquisition
   * step.
   * @param {Number} start - The timestamp of the starting date.
   * @param {Number} end - The timestamp of the ending date.
   * **Called by:
   * @see {PeriodImageChooser} - MEPBay module's period/image chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type SET_PERIOD.
   */
  setPeriod: (start, end) => ({
    type: Types.SET_PERIOD,
    payload: { start, end },
  }),

  /**
   * @function acquireImage
   * @description Builds the action requesting a single image from the orbital cycle started at
   * the specified date.
   * @param {String} missionName - Name of the mission to acquire from.
   * @param {String} date - Start date of the orbital cycle.
   * @param {...*} extras - Extra arguments forwarded in the payload.
   * **Called by:
   * @see {ImageChooserCardMepBay}
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type ACQUIRE_IMAGE.
   */
  acquireImage: (missionName, date, ...extras) => ({
    type: Types.ACQUIRE_IMAGE,
    payload: { missionName, date, extras },
  }),

  /**
   * @function requestAOI
   * @description Builds the action requesting the current AOI. Not currently dispatched from any
   * component.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type REQUEST_AOI.
   */
  requestAOI: () => ({
    type: Types.REQUEST_AOI,
  }),

  /**
   * @function loadThumbnails
   * @description Builds the action requesting thumbnails. Scoped to the given images if provided,
   * otherwise falls back to whatever is currently in state.mepbay.availableDates.
   * @param {Array<{name: string, date: string}>} [images]
   * **Called by:
   * @see {PeriodImageChooser} - MEPBay module's period/image chooser step.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type LOAD_THUMBNAILS.
   */
  loadThumbnails: (images) => ({
    type: Types.LOAD_THUMBNAILS,
    payload: { images },
  }),

  cancelThumbnails: () => ({
    type: Types.CANCEL_THUMBNAILS,
  }),

  /**
   * @function insertMetadata
   * @description Builds the action storing metadata for a specific mission/date image.
   * @param {String} missionName - Name of the mission.
   * @param {String} date - Image date.
   * @param {Object} metadata - Metadata to store.
   * **Called by:
   * @see {handleAcquireImage} - MEPBay module's saga.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type INSERT_METADATA.
   */
  insertMetadata: (missionName, date, metadata) => ({
    type: Types.INSERT_METADATA,
    payload: { missionName, date, metadata },
  }),

  /**
   * @function loadTestState
   * @description Builds the action that loads the default testing state into the state tree.
   * Not currently dispatched from any component.
   * @memberof module:Mepbay
   * @returns {Object} Redux action of type LOAD_TEST_STATE.
   */
  loadTestState: () => ({
    type: Types.LOAD_TEST_STATE,
  }),
};

export default Actions;
